import { toaster } from '@/components/ui/toaster';
import { conversationQueryKeys } from '@/hooks/queries/conversation-query-keys';
import api from '@/libs/axiosInstance';
import type {
  ApiErrorResponse,
  ApiResponse,
  SendInvitationOutreachEmailRequest,
  SendInvitationOutreachEmailResponse,
} from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { isPlanLimitError } from '@/libs/plan-limit-refusal';

export interface SendInvitationOutreachEmailParams extends SendInvitationOutreachEmailRequest {
  invitationId: string;
}

export function useSendInvitationOutreachEmail() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<
    SendInvitationOutreachEmailResponse,
    AxiosError<ApiErrorResponse>,
    SendInvitationOutreachEmailParams
  >({
    mutationFn: async ({
      invitationId,
      subject,
      body,
      attachmentIds,
      baseTemplateId,
      aiPersonalizationEnabled,
      kind,
    }): Promise<SendInvitationOutreachEmailResponse> => {
      const response = await api.post<ApiResponse<SendInvitationOutreachEmailResponse>>(
        `/dashboard/affiliates/invitations/${invitationId}/outreach-email`,
        {
          subject,
          body,
          attachmentIds,
          ...(baseTemplateId !== undefined ? { baseTemplateId } : {}),
          ...(aiPersonalizationEnabled !== undefined ? { aiPersonalizationEnabled } : {}),
          ...(kind !== undefined ? { kind } : {}),
        }
      );

      if (!response.data.data) {
        throw new Error('Invalid response structure');
      }

      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conversationQueryKeys.root });
    },
    onError: (error) => {
      // A plan refusal (a prospect whose address the plan withholds) opens the
      // upsell from the API client; a toast would repeat it.
      if (isPlanLimitError(error)) return;
      const errorCode = error.response?.data?.code;
      const fallback = error.response?.data?.message ?? t('invitationOutreach.send.error.generic');
      const translated = errorCode ? t(`errors.${errorCode}`, { defaultValue: '' }) : '';

      toaster.create({
        title: t('invitationOutreach.send.error.title'),
        description: translated || fallback,
        type: 'error',
        duration: 6000,
      });
    },
  });
}
